import { GROUP_LIFE_PRODUCT_CODE } from '@/app/config/products.config';
import {
  groupLifeService,
  type GroupLifeCloneOrigin,
} from '@/services/group-life';
import { groupLifeProposalsService } from '@/services/proposals/group-life';
import {
  PORTAL_STATUS_LABELS,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import { Tag, Text } from '@noorden-insurtech/alba-design-system';
import { DateTime } from 'luxon';
import { useQueries } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowRight, CalendarDays } from 'lucide-react';

/** Labels de status de COTAÇÃO ausentes no PORTAL_STATUS_LABELS (só proposta). */
const QUOTE_STATUS_LABELS: Record<number, string> = {
  [PortalStatus.QuoteInProgress]: 'Em digitação',
  [PortalStatus.QuoteClosed]: 'Cotação Encerrada',
  [PortalStatus.QuoteCalculated]: 'Cotação Calculada',
  [PortalStatus.QuoteExpired]: 'Cotação Expirada',
  [PortalStatus.QuoteDeleted]: 'Cotação Excluída',
};

const VERSIONS_GRID = 'grid-cols-[1fr_1.3fr_1.4fr_1.1fr_0.4fr]';

/** Header idêntico ao do DataTable do design system: thead `bg-alba-gray-200`
 *  + `border-b-2 border-alba-gray-300`, células uppercase. */
const HEADER_CELL =
  'font-montserrat text-xs font-bold uppercase text-alba-black-500';

function statusLabel(statusPortal?: string): string {
  const status = Number(statusPortal);
  if (!Number.isFinite(status)) return '—';
  return PORTAL_STATUS_LABELS[status] ?? QUOTE_STATUS_LABELS[status] ?? '—';
}

function kindLabel(kind: GroupLifeCloneOrigin['kind']): string {
  return kind === 'PROPOSAL' ? 'Proposta' : 'Cotação';
}

/**
 * `clonedAt` é um ISO em UTC — exibimos no fuso local com hora cheia, porque
 * numa mesma negociação vários clones podem nascer no mesmo dia e só a data
 * não os distingue.
 */
function formatClonedAt(value?: string): string {
  if (!value) return '—';
  const parsed = DateTime.fromISO(value);
  return parsed.isValid ? parsed.toFormat('dd/MM/yyyy HH:mm:ss') : '—';
}

/** Linha da tabela — um elo da cadeia de clonagem (ou o registro aberto). */
type VersionRow = {
  policyId: string;
  kind: GroupLifeCloneOrigin['kind'];
  proposalNumber: string;
  /** Data em que ESTE registro foi criado (o clone da linha anterior). */
  createdAt?: string;
  /** Status já conhecido — só o registro atual; os demais são buscados. */
  knownStatus?: string;
  isCurrent: boolean;
  isOriginal: boolean;
};

function OpenLink({ row, productId }: { row: VersionRow; productId?: string }) {
  const className =
    'flex h-8 w-8 items-center justify-center rounded-lg text-alba-gray-600 hover:bg-alba-gray-100';
  const label = `Abrir ${kindLabel(row.kind).toLowerCase()} ${row.proposalNumber}`;

  if (row.kind === 'PROPOSAL') {
    return (
      <Link
        to='/proposals/$id/pme'
        params={{ id: row.policyId }}
        className={className}
        aria-label={label}
      >
        <ArrowRight className='h-4 w-4' />
      </Link>
    );
  }

  // Cotação PME: o link direto é o wizard em modo view; sem productId
  // disponível, cai no detalhe comum (que redireciona para o wizard).
  if (productId) {
    return (
      <Link
        to='/quotes/products/new/$id'
        params={{ id: row.policyId }}
        search={{
          productId,
          category: 'GROUP_LIFE_INSURANCE',
          productCode: GROUP_LIFE_PRODUCT_CODE,
          stepCode: 'CALCULATION',
          quoteFlow: 'view',
        }}
        className={className}
        aria-label={label}
      >
        <ArrowRight className='h-4 w-4' />
      </Link>
    );
  }
  return (
    <Link
      to='/quotes/$policyId'
      params={{ policyId: row.policyId }}
      className={className}
      aria-label={label}
    >
      <ArrowRight className='h-4 w-4' />
    </Link>
  );
}

type Props = {
  /** Cadeia de ancestrais, do pai imediato à raiz (`details.clonedFrom`). */
  clonedFrom: GroupLifeCloneOrigin[];
  /** Registro aberto — entra na tabela como a versão "Atual". */
  current: {
    policyId: string;
    kind: GroupLifeCloneOrigin['kind'];
    proposalNumber?: string;
    statusPortal?: string;
  };
  /** productId da rota atual — monta o link direto do wizard PME (cotações). */
  productId?: string;
};

/**
 * Seção "Versões" do detalhe de uma cotação/proposta clonada.
 *
 * A clonagem É o versionamento do PME: cada clone da cadeia é uma versão da
 * negociação. A tabela lista da ORIGINAL (mais antiga) até o registro aberto,
 * marcado como "Atual".
 *
 * Nota sobre datas: cada elo guarda a data em que o FILHO daquele elo foi
 * criado. Por isso a data do registro aberto é `clonedFrom[0].clonedAt`, e a do
 * ancestral `i` é `clonedFrom[i+1].clonedAt`. A original não tem data de clone
 * (não nasceu de um) e exibe "—".
 */
export function CloneVersionsSection({
  clonedFrom,
  current,
  productId,
}: Props) {
  // `clonedFrom` vem do pai imediato à raiz; a tabela mostra em ordem
  // cronológica (original primeiro), com o registro aberto por último.
  const ancestorRows: VersionRow[] = clonedFrom
    .map((ancestor, index) => ({
      policyId: ancestor.policyId,
      kind: ancestor.kind,
      proposalNumber: ancestor.proposalNumber || ancestor.policyId,
      createdAt: clonedFrom[index + 1]?.clonedAt,
      isCurrent: false,
      isOriginal: index === clonedFrom.length - 1,
    }))
    .reverse();

  const rows: VersionRow[] = [
    ...ancestorRows,
    {
      policyId: current.policyId,
      kind: current.kind,
      proposalNumber: current.proposalNumber || current.policyId,
      createdAt: clonedFrom[0]?.clonedAt,
      knownStatus: current.statusPortal,
      isCurrent: true,
      isOriginal: clonedFrom.length === 0,
    },
  ];

  // Status atual de cada ancestral — o registro aberto já tem o seu.
  const statusQueries = useQueries({
    queries: ancestorRows.map(row => ({
      queryKey: ['group-life', 'clone-origin-status', row.kind, row.policyId],
      staleTime: 60_000,
      queryFn: async () => {
        const response =
          row.kind === 'PROPOSAL'
            ? await groupLifeProposalsService.getProposalDetails(row.policyId)
            : await groupLifeService.getQuoteDetails(row.policyId);
        if (!response.success || !response.data) {
          throw new Error(response.error || 'Erro ao consultar a versão');
        }
        return response.data.statusPortal ?? '';
      },
    })),
  });

  if (clonedFrom.length === 0) return null;

  const resolveStatus = (row: VersionRow, index: number): string => {
    if (row.isCurrent) return statusLabel(row.knownStatus);
    const query = statusQueries[index];
    if (query?.isLoading) return '…';
    return statusLabel(query?.data);
  };

  return (
    <div className='flex w-full flex-col gap-4'>
      <Text className='text-base text-alba-black-500'>Versões</Text>

      <div className='overflow-x-auto'>
        <div className='min-w-[720px] overflow-hidden rounded-xl border border-alba-gray-200'>
          <div
            className={`grid ${VERSIONS_GRID} gap-3 border-b-2 border-alba-gray-300 bg-alba-gray-200 px-4 py-4`}
          >
            <Text className={HEADER_CELL}>Tipo</Text>
            <Text className={HEADER_CELL}>Número</Text>
            <Text className={HEADER_CELL}>Data do clone</Text>
            <Text className={HEADER_CELL}>Status atual</Text>
            <span />
          </div>

          {rows.map((row, index) => (
            <div
              key={`${row.policyId}-${row.isCurrent ? 'current' : 'ancestor'}`}
              className={`grid ${VERSIONS_GRID} items-center gap-3 px-4 py-4 ${
                index > 0 ? 'border-t border-alba-gray-200' : ''
              } ${row.isCurrent ? 'bg-alba-gray-100/40' : ''}`}
            >
              <div className='flex flex-col items-start gap-1'>
                <Text
                  className={`font-montserrat text-sm text-alba-black-500 ${
                    row.isCurrent ? 'font-bold' : ''
                  }`}
                >
                  {kindLabel(row.kind)}
                </Text>
                {row.isCurrent ? (
                  <Tag.Root variant='solid' colorScheme='blue' size='sm'>
                    <Tag.Label>Atual</Tag.Label>
                  </Tag.Root>
                ) : row.isOriginal ? (
                  <Tag.Root variant='outline' colorScheme='gray' size='sm'>
                    <Tag.Label>Original</Tag.Label>
                  </Tag.Root>
                ) : null}
              </div>
              <Text
                className={`font-montserrat text-sm ${
                  row.isCurrent
                    ? 'font-bold text-alba-black-500'
                    : 'text-alba-gray-600'
                }`}
              >
                {row.proposalNumber}
              </Text>
              <div
                className={`flex items-center gap-2 font-montserrat text-sm ${
                  row.isCurrent
                    ? 'font-bold text-alba-black-500'
                    : 'text-alba-gray-600'
                }`}
              >
                <CalendarDays className='h-4 w-4 shrink-0 text-alba-gray-600' />
                <span className='whitespace-nowrap'>
                  {formatClonedAt(row.createdAt)}
                </span>
              </div>
              <Text className='font-montserrat text-sm text-alba-gray-600'>
                {resolveStatus(row, index)}
              </Text>
              {row.isCurrent ? (
                <span />
              ) : (
                <OpenLink row={row} productId={productId} />
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
